import css from './sandbox.module.css'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { ISandboxState } from './sandbox.types'
import { IDraftAudio, ITask, ITaskGroup } from '../../types/tasks.types'
import Button from '../../shared/Buttons/Button'
import TaskSandbox from './components/TaskSandbox'
import { useRecoilState, useRecoilValue } from 'recoil'
import sandboxAtom from './sandbox.atom'
import sandboxUtils from './sandbox.utils'
import useNavigationActions from '../Navigation/useNavigationActions'
import { ReactComponent as ArrowDownIcon } from '../../asserts/icons/arrowDown.svg'
import AudioUpload from './components/AudioUpload'
import useHttpLoader from '../../hooks/useHttpLoader'
import tasksService from '../../services/tasks/tasks.service'
import packsService from '../../services/packs/packs.service'
import navigationAtom from '../Navigation/navigation.atom'
import ThreeDotsLoader from '../../shared/Loaders/ThreeDotsLoader'
import { ReactComponent as Arrow } from '../../asserts/icons/arrowRight.svg'
import SaveConfirmModal from './components/SaveConfirmModal'
import { deepEqual } from '../../utils/misc.utils'
import Navigation from '../Navigation'
import SelectTaskTypeModal from './components/SelectTaskTypeModal'
import taskDescriptionTexts from './components/TaskDescription/taskDescriptionTexts'
import { ReactComponent as EditIcon } from '../../asserts/icons/edit.svg'
import UpdateGroupNameModal from './components/UpdateGroupNameModal'
import produce from 'immer'

const Sandbox = () => {
  const { wait, loading } = useHttpLoader()
  const [state, setState] = useRecoilState<ISandboxState>(sandboxAtom)

  const { next, prev } = useNavigationActions()
  const { level } = useRecoilValue(navigationAtom)

  const [confirmMoveModalOpen, setConfirmMoveModalOpen] = useState(false)
  const [selectTaskModalOpen, setSelectTaskModalOpen] = useState(false)
  const [editGroupNameModalOpen, setEditGroupNameModalOpen] = useState(false)

  const selectedGroup = useMemo(() => {
    return state.groups.find((g) => g.id === state.draftTask.group)
  }, [state.draftTask, state.groups])

  const handleGroupUpdated = (g: ITaskGroup) => {
    setState((prev) =>
      produce(prev, (draft) => {
        const index = draft.groups.findIndex((gr) => gr.id === g.id)
        draft.groups[index].name = g.name

        return draft
      })
    )
  }

  useEffect(() => {
    setState((prev) => ({ ...prev, draftTask: prev.tasks.find((t) => t.id === level) }))
  }, [level])

  const handleSave = () => {
    if (confirmMoveModalOpen) setConfirmMoveModalOpen(false)
    const prevTask = state.tasks.find((t) => t.id === state.draftTask.id)

    wait(tasksService.updateTask({ ...state.draftTask }, { ...prevTask }), (resp) => {
      if (resp) {
        setState((prev) => ({ ...prev, tasks: sandboxUtils.immutableUpdateTask(state.tasks, resp), draftTask: resp }))
      }
    })
  }

  const handleMove = (type: 'next' | 'prev') => () => {
    const prevTask = state.tasks.find((t) => t.id === state.draftTask.id)

    if (!deepEqual(prevTask, state.draftTask)) {
      setConfirmMoveModalOpen(true)
    } else {
      if (type === 'next') next()
      else prev()
    }
  }

  const handleDeploy = () => {
    wait(packsService.deployPack(state.version.id))
  }

  const handleTaskTypeSelect = useCallback((type: ITask['type']) => {
    setState((prev) => ({
      ...prev,
      draftTask: sandboxUtils.generateSandboxTask({ ...prev.draftTask, type }),
    }))
    setSelectTaskModalOpen(false)
  }, [])

  const handleDraftTaskChange = useCallback(<T extends keyof ITask>(value: ITask[T], key: T) => {
    setState((prev) => ({ ...prev, draftTask: { ...prev.draftTask, [key]: value } }))
  }, [])

  const handleDescriptionAudio = useCallback((audio: IDraftAudio) => {
    setState((prev) => {
      if (audio) {
        return {
          ...prev,
          draftTask: {
            ...prev.draftTask,
            audio: [...(prev.draftTask.audio as any[]).filter((a) => a.type !== 'TASK_DESCRIPTION'), audio],
          },
        }
      } else {
        return {
          ...prev,
          draftTask: {
            ...prev.draftTask,
            audio: (prev.draftTask.audio as any[]).filter((a) => a.type !== 'TASK_DESCRIPTION'),
          },
        }
      }
    })
  }, [])

  const renderFooter = () => {
    if (loading) {
      return (
        <div className={css.footer} style={{ justifyContent: 'center' }}>
          <ThreeDotsLoader />
        </div>
      )
    }

    return (
      <div className={css.footer}>
        <Button onClick={handleDeploy} loading={loading}>
          Деплой
        </Button>
        <Button onClick={handleSave} loading={loading}>
          Сохранить
        </Button>
      </div>
    )
  }

  return (
    <Navigation>
      <div className={css.wrapper}>
        <Arrow onClick={handleMove('prev')} className={css.arrowLeft} />
        <Arrow onClick={handleMove('next')} className={css.arrowRight} />
        {confirmMoveModalOpen && (
          <SaveConfirmModal onSave={handleSave} onContinue={next} onClose={() => setConfirmMoveModalOpen(false)} />
        )}
        {editGroupNameModalOpen && (
          <UpdateGroupNameModal
            group={selectedGroup}
            onClose={() => setEditGroupNameModalOpen(false)}
            onUpdated={(g) => {
              handleGroupUpdated(g)
              setEditGroupNameModalOpen(false)
            }}
          />
        )}
        {selectTaskModalOpen && (
          <SelectTaskTypeModal
            type={state?.draftTask?.type}
            onChange={handleTaskTypeSelect}
            onClose={() => setSelectTaskModalOpen(false)}
          />
        )}

        <div className={css.heading}>
          <div className={css.header}>
            <h2 style={{ fontSize: '36px', fontWeight: 'bold' }}>Редактор</h2>
            {selectedGroup && (
              <div className="inline-flex-gap" style={{ gap: '8px', alignItems: 'flex-end' }}>
                <h3 className={css.versionName}>{selectedGroup.name || 'Имя группы не задано'}</h3>
                <EditIcon
                  style={{ cursor: 'pointer', width: '20px', height: '20px' }}
                  onClick={() => setEditGroupNameModalOpen(true)}
                />
              </div>
            )}
          </div>
          <AudioUpload
            onAudio={handleDescriptionAudio}
            name="audio"
            key={state.draftTask.id}
            audio={(state.draftTask.audio as any[]).filter((a) => a.type === 'TASK_DESCRIPTION')[0]}
            active={!loading}
          />
          {state.draftTask.type !== 8 && state.draftTask.type !== 16 && (
            <div className={css.taskTypeSelector} onClick={() => setSelectTaskModalOpen(true)}>
              <h3 style={{ fontSize: '24px' }}>ТИП ЗАДАНИЯ</h3>
              <div className="inline-flex-gap">
                <h2 style={{ fontSize: '40px', color: 'var(--success)', flexShrink: 0 }}>
                  {state?.draftTask?.type ? state.draftTask.type + 1 : 1}
                </h2>
                <p className="line-clamp-2" title={taskDescriptionTexts[state.draftTask.type]}>
                  {taskDescriptionTexts[state.draftTask.type] || ''}
                </p>
                <ArrowDownIcon style={{ width: '80px', height: '40px', marginTop: 'auto' }} />
              </div>
            </div>
          )}
        </div>
        <div style={{ width: '1240px', marginLeft: 'auto', marginRight: 'auto' }}>
          <TaskSandbox
            taskType={state?.draftTask?.type}
            onDraftTaskChange={handleDraftTaskChange}
            key={state.draftTask.id}
          />
          {renderFooter()}
        </div>
      </div>
    </Navigation>
  )
}

export default Sandbox
